import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Form,
  InputNumber,
  DatePicker,
  Skeleton,
  Space,
  Typography,
  Empty,
  Checkbox,
  Tag,
  Spin,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { ThunderboltFilled, FileAddOutlined, EditOutlined, WarningFilled } from '@ant-design/icons';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import ClienteCompleteSelect from 'components/selects/ClienteCompleteSelect';
import BudgetEditor from './BudgetEditor';
import BudgetImagesField from './BudgetImagesField';
import { budgetImagesToFileList, fileListToImageIds, countLegacyImages } from './budgetImages';
import useFiles from 'hooks/useFiles';
import useTenantFlags from 'hooks/useTenantFlags';
import { getAccessTokenApi } from 'api/auth';
import { createBudgetApi, updateBudgetApi, getItemsApi, getImagesApi } from 'api/budget';
import { getHallazgosPendientesByClientApi } from 'api/hallazgoFlujo';
import { getGarantiasApi } from 'api/garantias';
import { resolveNamesToIds } from 'components/specialist/AttentionVoucher/AddAttentionVoucherForm/resolveNamesToIds';
import notificationApi from 'utils/notificationApi';
import { useTenantColors } from 'hooks/useTenantColors';
import { BASE_PATH_SERVER } from 'api/config';

// Referencia estable para el default de `hallazgos`. Sin esto, cada render
// del parent que no pase la prop crea un array nuevo como default, haciendo
// que los useEffect con `hallazgos` en deps disparen loops infinitos.
const EMPTY_HALLAZGOS = [];

const hexToRgba = (hex, alpha = 0.18) => {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return `rgba(148, 163, 184, ${alpha})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// El backend persiste `elevator` como CSV ("Asc1, Asc2") para soportar N
// ascensores por item sin tabla N:M. El multi-select del form trabaja con
// array. Estos helpers hacen el roundtrip.
const toElevatorArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};
const toElevatorString = (arr) => {
  if (Array.isArray(arr)) return arr.filter(Boolean).join(', ');
  return arr || '';
};

// Shell Drawer para crear/editar presupuestos. Modos:
// - Create from-hallazgos: recibe `hallazgos[]`, cliente derivado del primero.
// - Create con idClient dado: cliente fijo, sin hallazgos precargados.
// - Create standalone puro: sin idClient/hallazgos → selector de cliente +
//   panel de hallazgos pendientes opcionales.
// - Edit: recibe `budget` con el registro existente → carga items del backend,
//   precarga el form, muestra campo descuento, al submit hace PUT.
function BudgetEditorDrawer({
  open,
  hallazgos = EMPTY_HALLAZGOS,
  idClient: idClientProp,
  clienteNombre: clienteNombreProp,
  budget,
  onClose,
  onSuccess,
}) {
  const isEdit = Boolean(budget?.idBudget);
  // C3 (review 2026-07-13): el PDF de v1/v2 no sabe leer `guarantee_text` (el
  // snapshot del catálogo) — solo el texto libre `guarantee`. Un presupuesto
  // nuevo siempre nace en v3 (el backend fija `version: 3.0` al crear), así
  // que el catálogo se ofrece siempre en creación; en edición depende de la
  // versión real del presupuesto.
  const showCatalogoGarantia = !isEdit || Number(budget?.version) >= 3;
  // Bloqueante 1 (verificador feature garantías, 2026-07-13): un presupuesto
  // `aprobado` congela su cláusula de garantía en el backend (`updateBudget`
  // no la re-resuelve, ver presupuesto.srv.ts). Si dejamos el catálogo
  // habilitado acá, el usuario cambia la garantía, ve "Presupuesto
  // actualizado correctamente" y el cambio se descarta en silencio.
  // Deshabilitamos los campos del catálogo (no el resto del formulario) y
  // explicamos por qué.
  const isGuaranteeLocked = isEdit && budget?.status === 'aprobado';
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedHallazgos, setSelectedHallazgos] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [pendingHallazgos, setPendingHallazgos] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [pickedPendingIds, setPickedPendingIds] = useState([]);
  const [monedaState, setMonedaState] = useState({ moneda: 'CLP', tipoCambio: null });

  // Catálogo de equipos del cliente. Cacheado en memoria mientras el drawer
  // está abierto. Usado para resolver nombres → IDs al submit (Fase 4 del plan
  // trazabilidad-equipos-fk-migracion). Si el catálogo no cargó o un nombre
  // no resuelve, cada item individual cae al path legacy CSV.
  const [clientAscensores, setClientAscensores] = useState([]);

  // Catálogo de garantías del tenant (Task 8: GET /garantias, solo activas).
  // Alimenta el selector "Tipo de garantía" de BudgetEditor.
  const [garantias, setGarantias] = useState([]);

  // Imágenes del presupuesto en edición. `imagesLoaded` evita reemplazarlas
  // si no se pudieron cargar; `legacyImagesCount` = imágenes antiguas sin
  // fileId, que el backend conserva siempre.
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [legacyImagesCount, setLegacyImagesCount] = useState(0);
  const { getDownloadUrl } = useFiles();
  const { readOnly } = useTenantFlags();

  const { secondary } = useTenantColors();
  const secondaryTint = hexToRgba(secondary, 0.18);

  const isFromHallazgos = hallazgos.length > 0 || selectedHallazgos.length > 0;
  const needsClientSelection = !idClientProp && hallazgos.length === 0;

  useEffect(() => {
    if (!open) return;
    setSelectedHallazgos(hallazgos);
    setSelectedCliente(null);
    setPendingHallazgos([]);
    setPickedPendingIds([]);

    setImagesLoaded(false);
    setLegacyImagesCount(0);

    if (isEdit) {
      setMonedaState({ moneda: budget.moneda || 'CLP', tipoCambio: null });
      setLoadingItems(true);
      const token = getAccessTokenApi();
      Promise.all([
        getItemsApi(token, budget.idBudget),
        getImagesApi(token, budget.idBudget).catch(() => null),
      ]).then(([result, imagesResult]) => {
        const items = (result?.items || []).map((it) => ({
          elevator: toElevatorArray(it.elevator),
          description: it.description || '',
          unitValue: Number(it.unitValue || 0),
          quantity: Number(it.quantity || 0),
          id_coleccion_item: it.id_coleccion_item ?? null,
          // BUG 3: precargar el vínculo para preservarlo al editar (round-trip).
          id_hallazgo: it.id_hallazgo ?? null,
        }));
        form.resetFields();
        form.setFieldsValue({
          reason: budget.reason || '',
          description: budget.description || '',
          conditions: budget.conditions || '',
          guarantee: budget.guarantee || '',
          guaranteeId: budget.guarantee_id ?? undefined,
          guaranteeMonths: budget.guarantee_months ?? undefined,
          finishTime: budget.finishTime || '',
          discount: budget.discount != null ? Number(budget.discount) : 0,
          valid_until: budget.valid_until ? dayjs(budget.valid_until) : null,
          items,
          images: budgetImagesToFileList(imagesResult?.images, getDownloadUrl),
        });
        setImagesLoaded(Array.isArray(imagesResult?.images));
        setLegacyImagesCount(countLegacyImages(imagesResult?.images));
        setLoadingItems(false);
      });
      return;
    }

    setMonedaState({ moneda: 'CLP', tipoCambio: null });
    form.resetFields();
    form.setFieldsValue({
      reason: '',
      description: '',
      conditions: '',
      guarantee: '',
      finishTime: '',
      images: [],
      items: hallazgos.length
        ? hallazgos.map((h) => ({
            elevator: h.Ascensor?.nombre ? [h.Ascensor.nombre] : [],
            description: `Resolver hallazgo: ${h.descripcion}`.slice(0, 500),
            unitValue: 0,
            quantity: 1,
            // BUG 3: seed 1:1 — cada ítem sembrado resuelve su hallazgo. Editable
            // por el usuario con el selector por ítem del BudgetEditor.
            id_hallazgo: h.id,
          }))
        : [],
    });
  }, [open, hallazgos, form, isEdit, budget, getDownloadUrl]);

  // Carga el catálogo de garantías del tenant al abrir el drawer (create y
  // edit). Si la API falla, dejamos el selector vacío ("Sin garantía") en vez
  // de romper el form.
  useEffect(() => {
    if (!open) return;
    const token = getAccessTokenApi();
    getGarantiasApi(token).then((result) => {
      setGarantias(Array.isArray(result) ? result : []);
    });
  }, [open]);

  // Cuando el usuario elige un cliente en modo standalone, cargamos sus
  // hallazgos pendientes (etapa DETECTADO) para que opcionalmente los sume
  // al presupuesto y convierta el flow en from-hallazgos.
  useEffect(() => {
    if (!open || !selectedCliente?.id || hallazgos.length > 0) return;
    let cancel = false;
    setLoadingPending(true);
    getHallazgosPendientesByClientApi(selectedCliente.id)
      .then((res) => {
        if (cancel) return;
        setPendingHallazgos(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (!cancel) setPendingHallazgos([]);
      })
      .finally(() => {
        if (!cancel) setLoadingPending(false);
      });
    return () => {
      cancel = true;
    };
  }, [open, selectedCliente?.id, hallazgos.length]);

  const removeHallazgo = (id) => {
    setSelectedHallazgos((prev) => prev.filter((h) => h.id !== id));
    setPickedPendingIds((prev) => prev.filter((x) => x !== id));
  };

  const togglePendingHallazgo = (h, checked) => {
    const current = form.getFieldValue('items') || [];
    if (checked) {
      setPickedPendingIds((prev) => [...prev, h.id]);
      setSelectedHallazgos((prev) => [...prev, h]);
      form.setFieldValue('items', [
        ...current,
        {
          elevator: h.Ascensor?.nombre ? [h.Ascensor.nombre] : [],
          description: `Resolver hallazgo: ${h.descripcion}`.slice(0, 500),
          unitValue: 0,
          quantity: 1,
          id_hallazgo: h.id,
        },
      ]);
    } else {
      setPickedPendingIds((prev) => prev.filter((x) => x !== h.id));
      setSelectedHallazgos((prev) => prev.filter((x) => x.id !== h.id));
      // Removemos el ultimo item cuyo description matchea — heuristica simple;
      // si el usuario ya editó el item, dejamos que lo quite manualmente.
      const marker = `Resolver hallazgo: ${h.descripcion}`.slice(0, 500);
      const idx = current.findIndex((it) => it?.description === marker);
      if (idx >= 0) {
        const next = [...current];
        next.splice(idx, 1);
        form.setFieldValue('items', next);
      }
    }
  };

  const idClient =
    idClientProp ??
    (selectedHallazgos.length > 0
      ? selectedHallazgos[0].id_cliente ?? selectedHallazgos[0].Cliente?.id
      : null) ??
    selectedCliente?.id ??
    null;

  const clienteNombre =
    clienteNombreProp ?? selectedCliente?.name ?? selectedCliente?.razonSocial ?? null;

  // Pre-fetch del catálogo de equipos del cliente para resolver nombres → IDs
  // al submit (Fase 4 plan trazabilidad-equipos). Se gatilla cuando cambia
  // idClient o se abre el drawer.
  useEffect(() => {
    if (!open || !idClient) {
      setClientAscensores([]);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_PATH_SERVER}clientes/${idClient}/ascensores`, {
          headers: { Authorization: getAccessTokenApi() },
        });
        const data = await res.json();
        if (cancel) return;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.ascensores)
          ? data.ascensores
          : [];
        setClientAscensores(list);
      } catch {
        if (!cancel) setClientAscensores([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, idClient]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    if ((form.getFieldValue('images') || []).some((f) => f?.status === 'uploading')) {
      message.warning('Espere a que terminen de subir las imágenes');
      return;
    }
    if (!isEdit && !idClient) {
      message.error('No se pudo determinar el cliente del presupuesto');
      return;
    }
    setSubmitting(true);
    const values = form.getFieldsValue();
    const { moneda, tipoCambio } = monedaState;

    try {
      if (isEdit) {
        const token = getAccessTokenApi();
        const payload = {
          reason: values.reason || undefined,
          description: values.description || undefined,
          conditions: values.conditions || undefined,
          // C1/updateBudget es un full-replace: guarantee/guaranteeId/
          // guaranteeMonths se mandan SIEMPRE, incluso en null/'', o se
          // pierden en el PUT (bug crítico corregido en el review: editar un
          // presupuesto histórico le borraba la garantía).
          guarantee: values.guarantee || null,
          guaranteeId: values.guaranteeId ?? null,
          guaranteeMonths: values.guaranteeMonths ?? null,
          include: budget.include || undefined,
          finishTime: values.finishTime?.trim() || undefined,
          discount: Number(values.discount || 0),
          items: (values.items || []).map((it) => {
            // Plan trazabilidad-equipos Fase 4: si todos los nombres resuelven
            // contra el catálogo, enviamos id_ascensores (FK canonical). Si
            // alguno no resuelve (ej. texto libre histórico), fallback a CSV.
            const { ids, allResolved } = resolveNamesToIds(it.elevator, clientAscensores);
            return {
              ...(allResolved && ids.length > 0
                ? { id_ascensores: ids }
                : { elevator: toElevatorString(it.elevator) || undefined }),
              description: it.description || '',
              unitValue: Number(it.unitValue || 0),
              quantity: Number(it.quantity || 0),
              // BUG 3: preservar el vínculo hallazgo↔ítem en el edit.
              id_hallazgo: it.id_hallazgo ?? null,
            };
          }),
          moneda,
          tipo_cambio_snapshot: moneda === 'CLP' ? null : tipoCambio?.valor ?? null,
          // Fase 2: vigencia. Si no se toca el campo, se preserva el valor actual.
          valid_until: values.valid_until ? values.valid_until.toISOString() : undefined,
          // Si las imágenes no cargaron, se omite el campo: el backend las preserva.
          ...(imagesLoaded ? { images: fileListToImageIds(values.images) } : {}),
        };
        const result = await updateBudgetApi(token, payload, budget.idBudget);
        if (notificationApi(result)) {
          onSuccess?.(result);
        } else {
          message.error(result?.message || 'No se pudo editar el presupuesto');
        }
        return;
      }

      const token = getAccessTokenApi();
      const payload = {
        idClient,
        ...(selectedHallazgos.length > 0
          ? { hallazgoIds: selectedHallazgos.map((h) => h.id) }
          : {}),
        reason: values.reason || undefined,
        description: values.description || undefined,
        conditions: values.conditions || undefined,
        // Siempre presentes (incluso null) para consistencia con el update.
        guarantee: values.guarantee || null,
        guaranteeId: values.guaranteeId ?? null,
        guaranteeMonths: values.guaranteeMonths ?? null,
        finishTime: values.finishTime?.trim() || undefined,
        items: (values.items || []).map((it) => {
          const { ids, allResolved } = resolveNamesToIds(it.elevator, clientAscensores);
          return {
            ...(allResolved && ids.length > 0
              ? { id_ascensores: ids }
              : { elevator: toElevatorString(it.elevator) || undefined }),
            description: it.description || undefined,
            unitValue: Number(it.unitValue || 0),
            quantity: Number(it.quantity || 0),
            id_coleccion_item: it.id_coleccion_item ?? null,
            id_hallazgo: it.id_hallazgo ?? null,
          };
        }),
        moneda,
        tipo_cambio_snapshot: moneda === 'CLP' ? null : tipoCambio?.valor ?? null,
        // Fase 2: vigencia. Si se omite, el backend usa now + 30 días default.
        valid_until: values.valid_until ? values.valid_until.toISOString() : undefined,
        images: fileListToImageIds(values.images),
      };
      const result = await createBudgetApi(token, payload);
      if (result?.budget?.id) {
        const n = result.hallazgoIds?.length ?? 0;
        message.success(
          n > 0
            ? `Presupuesto creado (#${result.budget.id}) — ${n} hallazgos vinculados`
            : 'Presupuesto creado'
        );
        onSuccess?.(result);
      } else {
        message.error(result?.message || 'No se pudo crear el presupuesto');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    !loadingItems &&
    (isEdit || (!!idClient && (!isFromHallazgos || selectedHallazgos.length > 0)));

  return (
    <Drawer
      title={
        <Space size="middle" align="center">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: secondaryTint,
              color: secondary,
            }}
          >
            {isEdit ? (
              <EditOutlined style={{ fontSize: 18 }} />
            ) : (
              <FileAddOutlined style={{ fontSize: 18 }} />
            )}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
              {isEdit
                ? `Editar presupuesto #${budget.idBudget}`
                : isFromHallazgos
                ? 'Nuevo presupuesto desde hallazgos'
                : 'Nuevo presupuesto'}
            </div>
            {clienteNombre ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>{clienteNombre}</div>
            ) : null}
          </div>
        </Space>
      }
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            icon={isEdit ? <EditOutlined /> : <ThunderboltFilled />}
          >
            {isEdit ? 'Guardar cambios' : 'Crear presupuesto'}
          </Button>
        </Space>
      }
    >
      {!isEdit && needsClientSelection ? (
        <div style={{ marginBottom: 20 }}>
          <Typography.Text
            strong
            style={{
              fontSize: 11,
              letterSpacing: 0.8,
              color: '#64748b',
              display: 'block',
              marginBottom: 8,
            }}
          >
            CLIENTE
          </Typography.Text>
          <ClienteCompleteSelect onChange={setSelectedCliente} />
        </div>
      ) : null}

      {!isEdit && needsClientSelection && selectedCliente ? (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <Typography.Text strong style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}>
              HALLAZGOS PENDIENTES DE ESTE CLIENTE
              {pendingHallazgos.length > 0 ? ` · ${pendingHallazgos.length}` : ''}
            </Typography.Text>
            {pickedPendingIds.length > 0 ? (
              <Typography.Text type="success" style={{ fontSize: 11 }}>
                {pickedPendingIds.length} seleccionado
                {pickedPendingIds.length === 1 ? '' : 's'}
              </Typography.Text>
            ) : null}
          </div>
          {loadingPending ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <Spin size="small" />
            </div>
          ) : pendingHallazgos.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Este cliente no tiene hallazgos pendientes sin presupuestar.
            </Typography.Text>
          ) : (
            <div
              style={{
                border: '1px solid #e2e6eb',
                borderRadius: 8,
                background: '#fafbfc',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {pendingHallazgos.map((h) => {
                const checked = pickedPendingIds.includes(h.id);
                return (
                  <label
                    key={h.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '8px 12px',
                      borderBottom: '1px solid #eef1f4',
                      cursor: 'pointer',
                      background: checked ? '#f0f9ff' : 'transparent',
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => togglePendingHallazgo(h, e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: '#0f172a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {h.descripcion}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        {h.Ascensor ? (
                          <Tag style={{ fontSize: 10, marginInlineEnd: 0 }}>
                            {h.Ascensor.nombre}
                          </Tag>
                        ) : null}
                        {h.tipo ? (
                          <Tag
                            color={h.tipo === 'INCIDENCIA' ? 'red' : 'default'}
                            style={{ fontSize: 10, marginInlineEnd: 0 }}
                            icon={h.tipo === 'INCIDENCIA' ? <WarningFilled /> : null}
                          >
                            {h.tipo}
                          </Tag>
                        ) : null}
                        {h.severidad ? (
                          <Tag
                            color={
                              h.severidad === 'CRITICA' || h.severidad === 'ALTA'
                                ? 'red'
                                : h.severidad === 'MEDIA'
                                ? 'orange'
                                : 'default'
                            }
                            style={{ fontSize: 10, marginInlineEnd: 0 }}
                          >
                            {h.severidad}
                          </Tag>
                        ) : null}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {!isEdit && isFromHallazgos ? (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <Typography.Text strong style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}>
              HALLAZGOS QUE CUBRE · {selectedHallazgos.length}
            </Typography.Text>
          </div>
          {selectedHallazgos.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin hallazgos seleccionados. Cierra y selecciona al menos uno."
            />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectedHallazgos.map((h) => (
                <HallazgoChip key={h.id} hallazgo={h} onRemove={removeHallazgo} />
              ))}
            </div>
          )}
        </div>
      ) : null}

      <Skeleton loading={isEdit && loadingItems} active paragraph={{ rows: 8 }}>
        <Form form={form} layout="vertical" requiredMark={false}>
          {!isEdit && needsClientSelection && !idClient ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Seleccioná un cliente para empezar a cargar el presupuesto."
              style={{ marginTop: 40 }}
            />
          ) : (
            <>
              <BudgetEditor
                form={form}
                initialMoneda={isEdit ? budget?.moneda || 'CLP' : 'CLP'}
                onMonedaChange={setMonedaState}
                showCatalogoPicker
                idClient={isEdit ? budget?.idClient : idClient}
                hallazgos={selectedHallazgos}
                garantias={garantias}
                showCatalogoGarantia={showCatalogoGarantia}
                guaranteeLocked={isGuaranteeLocked}
              />
              <BudgetImagesField disabled={readOnly} legacyCount={legacyImagesCount} />
              {isEdit ? (
                <Form.Item
                  label="Descuento (0 a 1)"
                  help="0.2 representa 20% de descuento"
                  name="discount"
                  style={{ marginTop: 16 }}
                  rules={[
                    {
                      validator: (_, value) =>
                        value >= 0 && value < 1
                          ? Promise.resolve()
                          : Promise.reject(new Error('El descuento debe estar entre 0 y 1')),
                    },
                  ]}
                >
                  <InputNumber min={0} max={0.99} step={0.01} style={{ width: '100%' }} />
                </Form.Item>
              ) : null}
              {/* Fase 2: vigencia del presupuesto. Si se deja vacío, el backend
                  usa 30 días por defecto. */}
              <Form.Item
                label="Vigencia (vence el)"
                help="Si se deja vacío, vence en 30 días"
                name="valid_until"
                style={{ marginTop: 16 }}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </>
          )}
        </Form>
      </Skeleton>
    </Drawer>
  );
}

export default BudgetEditorDrawer;
